import React, { useState } from 'react';

import { ActionIcon, createStyles, Group, Paper, px, Title, Tooltip, useMantineTheme } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconFilterOff, IconPlus, IconSettings } from '@tabler/icons-react';
import ButtonMenu from 'components/ButtonMenu';
import EventTypeMultiSelectAutoComplete, {
  IEventTypeMultiSelectAutoComplete,
} from 'components/form-fields/auto-complete-fields/EventTypeMultiSelectAutoComplete';
import LocationMultiSelectAutoComplete from 'components/form-fields/auto-complete-fields/LocationMultiSelectAutoComplete';
import StaffMultiSelectAutoComplete from 'components/form-fields/auto-complete-fields/StaffMultiSelectAutoComplete';
import { useStaffTracking } from 'hooks/mixpanel/useStaffTracking';

import EmbedCalendarSettingsModal from './EmbedCalendarSettingsModal';

const useStyles = createStyles(theme => ({
  header: {
    padding: theme.spacing.lg,
    paddingBottom: 0,
    background: theme.colors.gray[0],
    borderRadius: 0,
    display: 'grid',
    gridTemplateColumns: 'auto auto auto auto min-content min-content',
    gridTemplateRows: 'auto',
    gridTemplateAreas: `"title search1 search2 search3 clear button"`,
    alignItems: 'center',
    gap: theme.spacing.sm,
    [theme.fn.smallerThan('xl')]: {
      gridTemplateColumns: 'auto auto auto min-content min-content',
      gridTemplateRows: 'auto auto',
      gridTemplateAreas: `
        'title title title button button'
        'search1 search2 search3 search3 clear'
      `,
    },
    [theme.fn.smallerThan('700px')]: {
      gridTemplateColumns: 'auto min-content min-content',
      gridTemplateRows: 'auto auto auto auto',
      gridTemplateAreas: `
        'title button button'
        'search1 search1 search1'
        'search2 search2 search2'
        'search3 search3 search3'
        '. . clear'
      `,
    },
  },
  title: {
    gridArea: 'title',
  },
  search1: {
    gridArea: 'search1',
  },
  search2: {
    gridArea: 'search2',
  },
  search3: {
    gridArea: 'search3',
  },
  clear: {
    gridArea: 'clear',
  },
  button: {
    gridArea: 'button',
  },
}));

interface FilterTerms {
  eventTypes: string[];
  coaches: string[];
  locations: string[];
}

interface ISchedulingPageHeaderProps {
  searchFilters: FilterTerms;
  initialSearchFiltersValue: FilterTerms;
  setSearchFilters: React.Dispatch<React.SetStateAction<FilterTerms>>;
  handleOpenCreateClassForm: () => void;
  handleOpenCreateAppointmentForm: () => void;
  permission?: boolean;
}

const SchedulingPageHeader = ({
  searchFilters,
  initialSearchFiltersValue,
  setSearchFilters,
  handleOpenCreateClassForm,
  handleOpenCreateAppointmentForm,
  permission = true,
}: ISchedulingPageHeaderProps): JSX.Element => {
  const theme = useMantineTheme();
  const { classes } = useStyles();
  const staffTracking = useStaffTracking();

  const [eventTypes, setEventTypes] = useState<IEventTypeMultiSelectAutoComplete[]>([]);
  const [embedModalOpened, { open: openEmbedModal, close: closeEmbedModal }] = useDisclosure(false);

  const handleEventTypesChange = (values: IEventTypeMultiSelectAutoComplete[]): void => {
    setEventTypes(values);
    const eventTypeIds = values.map(v => v.eventTypeId);
    setSearchFilters(prev => ({ ...prev, eventTypes: eventTypeIds }));
    staffTracking.trackcalendarFilterApplied('event_types', eventTypeIds.join(',') || 'none');
  };
  const handleCoachesChange = (values: string[]): void => {
    setSearchFilters(prev => ({ ...prev, coaches: values }));
    staffTracking.trackcalendarFilterApplied('coaches', values.join(',') || 'none');
  };
  const handleLocationsChange = (values: string[]): void => {
    setSearchFilters(prev => ({ ...prev, locations: values }));
    staffTracking.trackcalendarFilterApplied('locations', values.join(',') || 'none');
  };

  const handleClearFilters = (): void => {
    setSearchFilters(initialSearchFiltersValue);
    setEventTypes([]);
    staffTracking.trackcalendarFilterApplied('clear_all', 'all_cleared');
  };

  return (
    <Paper className={classes.header}>
      <Title order={1} size='h1' className={classes.title}>
        Scheduling
      </Title>
      <EventTypeMultiSelectAutoComplete
        handleChange={handleEventTypesChange}
        values={eventTypes}
        className={classes.search1}
      />
      <LocationMultiSelectAutoComplete
        onChange={handleLocationsChange}
        value={searchFilters.locations}
        className={classes.search2}
      />
      <StaffMultiSelectAutoComplete
        onChange={handleCoachesChange}
        value={searchFilters.coaches}
        isCoach
        maxSelectedValues={3}
        className={classes.search3}
      />
      <Group spacing='xs' className={classes.clear} noWrap>
        <Tooltip label='Embed Calendar Widget' offset={px(theme.spacing.md)}>
          <ActionIcon onClick={openEmbedModal} color='gray'>
            <IconSettings size={20} />
          </ActionIcon>
        </Tooltip>
        <Tooltip label='Clear filters' offset={px(theme.spacing.md)}>
          <ActionIcon onClick={handleClearFilters}>
            <IconFilterOff />
          </ActionIcon>
        </Tooltip>
      </Group>
      <Group spacing='xs' className={classes.button}>
        <ButtonMenu
          data-cy='addEventButton'
          title='Event'
          leftIcon={<IconPlus size={theme.fontSizes.md} />}
          disabled={!permission}
          items={[
            {
              label: 'Class',
              onClick: () => handleOpenCreateClassForm(),
              'data-cy': 'createClassButton',
            },
            {
              label: 'Appointment',
              onClick: () => handleOpenCreateAppointmentForm(),
              'data-cy': 'createAppointmentButton',
            },
          ]}
        />
      </Group>
      <EmbedCalendarSettingsModal opened={embedModalOpened} onClose={closeEmbedModal} />
    </Paper>
  );
};

export default SchedulingPageHeader;
